<script setup lang="ts">
import { useApp } from "@/vf"
//import { readXlsxFile } from "read-excel-file"
import VarsityAthleteTemplate from "@/assets/templates/VarsityAthleteTemplate.xltx?url"
import { zipFormats } from "@/formats"
import { useCountriesStore } from "@/stores/useCountryStore"
import { validateZip, ZipValidationMessage, type ZipValidationResult } from "@/utils"
import { defineTranslations } from "@/utils/defineTranslations"
import { computed, ref, toRefs } from "vue"
import { transformDate, transformGender } from "./transformers"

const { apiLink } = useApp()
const countries = useCountriesStore()

// pre-load the countries, this will get awaited in the upload handler
countries.ensureLoaded()

const props = defineProps<{
    formDocuments: string[]
    organizations: { id: string; name: string; label: string }[]
}>()
const { formDocuments, organizations } = toRefs(props)
const inputElement = ref<HTMLInputElement | null>(null)

const displayWrongFormatError = ref(false)
const displayWrongFileError = ref(false)

const athletes = ref<
    {
        firstName: string
        lastName: string
        birthDate: string
        gender: string
        street: string
        streetNumber: string
        zip: string
        city: string
        country: string
        // minorRepresentative: string
    }[]
>([])

function verifyHeadRows(rows: string[][]) {
    if (rows.length < 2) {
        console.log("Format Error: Not enough rows")
        return false
    }
    const germanHeader = rows[0]
    const englishHeader = rows[1]
    if (germanHeader.length !== 9) {
        console.log("Format Error: Wrong number of columns")
        return false
    }
    if (germanHeader.length !== englishHeader.length) {
        console.log("Format Error: Different number of columns")
        return false
    }
    return true
}

function readRows(rows: string[][]) {
    if (!verifyHeadRows(rows)) {
        displayWrongFormatError.value = true
        return
    }

    for (let i = 2; i < rows.length; i++) {
        const row = rows[i]

        if (row.length < 9) {
            continue
        }

        if (!row[0]?.trim()?.length && !row[1]?.trim()?.length) {
            continue
        }

        athletes.value.push({
            firstName: row[0],
            lastName: row[1],
            birthDate: row[2] ? transformDate(row[2])! : "",
            gender: row[3] ? transformGender(row[3]) : "",
            street: row[4],
            streetNumber: row[5],
            zip: row[6],
            city: row[7],
            country: row[8],
            // minorRepresentative: row[9],
        })
    }
}

async function readFile() {
    displayWrongFileError.value = false
    const file = inputElement.value?.files?.[0]
    if (!file) {
        return
    }

    const fileName = file.name.toLowerCase()

    // this is the latest point where we need the countries to be loaded, so if they are not loaded yet, we wait for them
    await countries.ensureLoaded()

    if (fileName.endsWith(".csv")) {
        const { parse } = await import("papaparse")
        const fr = new FileReader()

        fr.onload = event => {
            readRows(parse(event.target!.result as string).data as string[][])
        }

        fr.readAsText(file)

        return
    }

    if (fileName.endsWith(".xlsx")) {
        const { default: readXlsxFile } = await import("read-excel-file") // make sure to lazy load those dependencies
        readRows((await readXlsxFile(file)).map(row => row.map(cell => cell?.toString())))

        return
    }

    displayWrongFileError.value = true
}

const conditionIds = computed(() => {
    return formDocuments.value.join(",")
})

const selectedOrganization = ref<{ id: string; name: string; label: string }>(
    organizations.value[0] ?? {
        id: "",
        name: "",
        label: "",
    },
)

type ValidationResult = {
    zip: ZipValidationResult
    country: { valid: boolean }
    gender: { valid: boolean }
    valid: boolean
}

const validations = computed(() => {
    const result: Record<number, ValidationResult> = {}

    for (const [index, athlete] of athletes.value.entries()) {
        const zip = validateZip(athlete.zip, athlete.country)
        const country = { valid: athlete.country?.length == 2 && athlete.country in zipFormats }
        const genderValid = athlete.gender === "m" || athlete.gender === "f" || athlete.gender === "x"

        result[index] = {
            zip,
            country,
            gender: {
                valid: genderValid,
            },
            valid: zip.valid && country.valid && genderValid,
        }
    }

    return result
})
const allValid = computed(() => {
    return athletes.value.length === 0 || Object.values(validations.value).some(v => !v.valid)
})

const trans = defineTranslations({
    invalid_country_code: {
        de: "Ungültiger Ländercode. Bitte geben Sie einen gültigen Ländercode (2-stellig) ein.",
        en: "Invalid country code. Please enter a valid country code (2-letter).",
    },
    invalid_gender: {
        de: "Ungültiges Geschlecht. Bitte wählen Sie 'm' für männlich, 'w' für weiblich oder 'x' für divers.",
        en: "Invalid gender. Please select 'm' for male, 'f' for female or 'x' for diverse.",
    },
    hint_download: {
        de: "Hinweis: Das Herunterladen der Dateien kann einen Moment dauern.",
        en: "Note: Downloading the files may take a moment.",
    },
})
</script>

<template>
    <a class="btn btn-secondary" :href="VarsityAthleteTemplate">
        {{ $t("@cms:onlineForm.upload.downloadTemplate") }}
    </a>
    <label class="btn btn-primary">
        {{ $t("@cms:onlineForm.upload.selectFile") }}
        <input type="file" @change="readFile" ref="inputElement" accept=".csv,.xlsx" class="d-none" />
    </label>
    <div class="alert alert-danger" v-if="displayWrongFileError">{{ $t("@cms:onlineForm.upload.wrongFileType") }}</div>
    <div class="alert alert-danger" v-if="displayWrongFormatError">
        {{ $t("@cms:onlineForm.upload.wrongFileFormat") }}
    </div>
    <template v-if="athletes.length > 0">
        <select v-model="selectedOrganization" class="form-control" v-if="organizations.length > 1">
            <option v-for="organization of organizations" :value="organization" :key="organization.id">
                {{ organization.label }}
            </option>
        </select>
        <form method="post" :action="apiLink('legal/form/pdf/post-data/' + selectedOrganization.id)" target="_blank">
            <input type="hidden" name="c" :value="conditionIds" />
            <table class="table">
                <thead>
                    <tr>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.firstName") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.lastName") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.birthDate") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.gender") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.street") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.streetNumber") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.zip") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.city") }}</th>
                        <th>{{ $t("@cms:onlineForm.bunchAdd.country") }}</th>
                        <!--                    <th>{{ $t("@cms:onlineForm.bunchAdd.minorRepresentative") }}</th>-->
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(athlete, index) of athletes" :key="index">
                        <tr>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][firstName]'"
                                    v-model="athlete.firstName"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][lastName]'"
                                    v-model="athlete.lastName"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <input
                                    type="date"
                                    :name="'a[' + index + '][birthDate]'"
                                    v-model="athlete.birthDate"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <select
                                    class="form-control"
                                    :name="'a[' + index + '][gender]'"
                                    v-model="athlete.gender"
                                    :class="{ 'is-invalid text-danger': !validations[index].gender.valid }"
                                >
                                    <option value="m">{{ $t("@cms:onlineForm.bunchAdd.gender.male") }}</option>
                                    <option value="f">{{ $t("@cms:onlineForm.bunchAdd.gender.female") }}</option>
                                </select>
                            </td>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][street]'"
                                    v-model="athlete.street"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][streetNumber]'"
                                    v-model="athlete.streetNumber"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][zip]'"
                                    v-model="athlete.zip"
                                    class="form-control"
                                    :class="{ 'is-invalid text-danger': !validations[index].zip.valid }"
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    :name="'a[' + index + '][city]'"
                                    v-model="athlete.city"
                                    class="form-control"
                                />
                            </td>
                            <td>
                                <select
                                    class="form-control custom-select m-0"
                                    v-model="athlete.country"
                                    :name="'a[' + index + '][country]'"
                                    :class="{ 'is-invalid text-danger': !validations[index].country.valid }"
                                    style="padding-right: 2rem"
                                >
                                    <option
                                        v-for="[iso, _name] of countries.billingCountries.entries()"
                                        :key="iso"
                                        :value="iso"
                                    >
                                        {{ iso }}
                                    </option>
                                </select>
                            </td>
                            <!--                    <td>-->
                            <!--                        <input-->
                            <!--                            type="text"-->
                            <!--                            :name="'a[' + index + '][minorRepresentative]'"-->
                            <!--                            v-model="athlete.minorRepresentative"-->
                            <!--                            class="form-control"-->
                            <!--                        />-->
                            <!--                    </td>-->
                            <td>
                                <span v-if="!validations[index].valid" class="o-50 c-default">
                                    <i class="fa fa-download ml-3"></i>
                                </span>
                                <a
                                    :href="
                                        apiLink(
                                            'legal/form/pdf/query-data/' +
                                                selectedOrganization.id +
                                                '?c=' +
                                                conditionIds,
                                            athlete,
                                        )
                                    "
                                    target="_blank"
                                    v-if="validations[index].valid"
                                >
                                    <i class="fa fa-download ml-3"></i>
                                </a>
                            </td>
                        </tr>
                        <tr v-if="!validations[index].valid">
                            <td colspan="10" class="pt-3 pb-4 px-4">
                                <div class="text-danger" v-if="!validations[index].gender.valid">
                                    {{ trans("invalid_gender") }}
                                </div>
                                <div class="text-danger" v-if="!validations[index].country.valid">
                                    {{ trans("invalid_country_code") }}
                                </div>
                                <ZipValidationMessage :result="validations[index].zip" v-if="validations[index].zip" />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <button class="btn btn-primary" type="submit" :disabled="allValid">
                {{ $t("@cms:onlineForm.downloadAllAthletes") }}
            </button>
            <button class="btn btn-primary" type="button" @click="athletes = []">
                {{ $t("@cms:onlineForm.removeAllRows") }}
            </button>
            <div>
                {{ trans("hint_download") }}
            </div>
        </form>
    </template>
</template>

<style scoped>
table td {
    padding: 2px;
}

table th {
    text-align: center;
}
</style>
